import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import {
  KEY_GRANT_RESOURCE_TYPES,
  LIFE_DESIGN_SHARED_RESOURCE_TYPES,
  wrappedDekJson,
} from "~/lib/schemas/key-grants";

const resourceType = z.enum(KEY_GRANT_RESOURCE_TYPES);

const GetKeyGrantSchema = z.object({
  resourceType: z.string(),
  resourceId: z.string().uuid(),
});

const AddKeyGrantSchema = z.object({
  resourceType,
  resourceId: z.string().uuid(),
  userId: z.string().uuid(),
  encryptedDek: wrappedDekJson,
});

const RemoveKeyGrantSchema = z.object({
  resourceType,
  resourceId: z.string().uuid(),
  userId: z.string().uuid(),
});

/** Get the current user's DEK grant for a resource. */
export const getKeyGrantFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GetKeyGrantSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { and, eq } = await import("drizzle-orm");
    const { keyGrants } = await import("drizzle/schema");

    const db = await getDBWithInit();
    const userId = context.user.id;

    const [row] = await db
      .select({ encryptedDek: keyGrants.encryptedDek })
      .from(keyGrants)
      .where(
        and(
          eq(keyGrants.resourceType, data.resourceType),
          eq(keyGrants.resourceId, data.resourceId),
          eq(keyGrants.userId, userId),
        ),
      )
      .limit(1);

    return row?.encryptedDek ?? null;
  });

/** Give another person a record's key (sharing, key repair, Nova sync). */
export const addKeyGrantFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(AddKeyGrantSchema)
  .handler(async ({ data, context }) => {
    const { addKeyGrant } = await import("~/lib/server/key-grants");
    await addKeyGrant(context.user.id, data);
  });

/** Remove another person's key on a record (unsharing). */
export const removeKeyGrantFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(RemoveKeyGrantSchema)
  .handler(async ({ data, context }) => {
    const { removeKeyGrant } = await import("~/lib/server/key-grants");
    await removeKeyGrant(context.user.id, data);
  });

const RemoveKeyGrantsByGranteeSchema = z.object({
  resourceType: z.enum(LIFE_DESIGN_SHARED_RESOURCE_TYPES),
  granteeUserId: z.string().uuid(),
});

/** Remove a grantee's keys on every coach-shared record of a type the caller owns (bulk unshare). */
export const removeKeyGrantsByGranteeFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(RemoveKeyGrantsByGranteeSchema)
  .handler(async ({ data, context }) => {
    const { removeKeyGrantsByGrantee } =
      await import("~/lib/server/key-grants");
    await removeKeyGrantsByGrantee(context.user.id, data);
  });

const GetSharingContextSchema = z.object({
  resourceTypes: z.array(z.enum(LIFE_DESIGN_SHARED_RESOURCE_TYPES)),
});

/** The caller's own keys on the records they own that the "share with coaches" switches cover. */
export const getSharingContextFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GetSharingContextSchema)
  .handler(async ({ data, context }) => {
    const { listOwnSharedKeyGrants } = await import("~/lib/server/key-grants");
    return {
      ownGrants: await listOwnSharedKeyGrants(
        context.user.id,
        data.resourceTypes,
      ),
    };
  });

const GetEngagementCounterpartSchema = z.object({
  engagementId: z.string().uuid(),
});

/**
 * Resolve the other party of an engagement (coach ↔ client) for key-grant
 * sharing. Returns their userId + public key, or null if they haven't set up
 * encryption yet. Works for either side: the caller's role is inferred.
 */
export const getEngagementCounterpartFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GetEngagementCounterpartSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { eq } = await import("drizzle-orm");
    const { engagements, coaches, userEncryptionKeys } =
      await import("drizzle/schema");

    const db = await getDBWithInit();
    const callerUserId = context.user.id;

    const [engagement] = await db
      .select({
        userId: engagements.userId,
        coachUserId: coaches.userId,
      })
      .from(engagements)
      .innerJoin(coaches, eq(engagements.coachId, coaches.id))
      .where(eq(engagements.id, data.engagementId))
      .limit(1);
    if (!engagement) throw new Error("Engagement not found");

    let counterpartUserId: string | null;
    if (engagement.userId === callerUserId) {
      counterpartUserId = engagement.coachUserId;
    } else if (engagement.coachUserId === callerUserId) {
      counterpartUserId = engagement.userId;
    } else {
      throw new Error("Not a participant of this engagement");
    }

    if (!counterpartUserId) return null;

    const [keys] = await db
      .select({ publicKey: userEncryptionKeys.publicKey })
      .from(userEncryptionKeys)
      .where(eq(userEncryptionKeys.userId, counterpartUserId))
      .limit(1);
    if (!keys) return null;

    return { userId: counterpartUserId, publicKey: keys.publicKey };
  });

const GetActiveCoachUserIdsSchema = z.object({});

/** Get coaches with encryption keys linked to the current user via active engagements. */
export const getActiveCoachUserIdsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GetActiveCoachUserIdsSchema)
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { and, eq, inArray, isNotNull } = await import("drizzle-orm");
    const { engagements, coaches, userEncryptionKeys } =
      await import("drizzle/schema");
    const { EngagementStatus } = await import("~/lib/schemas/db-enums");

    const db = await getDBWithInit();
    const userId = context.user.id;

    const activeEngagements = await db
      .select({ coachId: engagements.coachId })
      .from(engagements)
      .where(
        and(
          eq(engagements.userId, userId),
          eq(engagements.status, EngagementStatus.ACTIVE),
        ),
      );

    if (activeEngagements.length === 0) return [];

    const coachIds = activeEngagements.map((e) => e.coachId);

    // Resolve coach → userId (only coaches that have a linked user account)
    const coachUserMappings = await db
      .select({ coachId: coaches.id, userId: coaches.userId })
      .from(coaches)
      .where(and(inArray(coaches.id, coachIds), isNotNull(coaches.userId)));

    if (coachUserMappings.length === 0) return [];

    const coachUserIds = coachUserMappings.map((c) => c.userId as string);

    // Get public keys for those user IDs
    const keysById = await db
      .select({
        userId: userEncryptionKeys.userId,
        publicKey: userEncryptionKeys.publicKey,
      })
      .from(userEncryptionKeys)
      .where(inArray(userEncryptionKeys.userId, coachUserIds));

    const keyMap = new Map(keysById.map((k) => [k.userId, k.publicKey]));

    return coachUserMappings.flatMap((mapping) => {
      const publicKey = keyMap.get(mapping.userId as string);
      if (!publicKey) return [];
      return [
        {
          coachId: mapping.coachId,
          userId: mapping.userId as string,
          publicKey,
        },
      ];
    });
  });
